import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Takeoff from "@/pages/Takeoff";
import Estimator from "@/pages/Estimator";
import Timesheet from "@/pages/Timesheet";
import Employees from "@/pages/Employees";
import Invoices from "@/pages/Invoices";
import InvoiceDetail from "@/pages/InvoiceDetail";
import CalendarPage from "@/pages/CalendarPage";
import Accounting from "@/pages/Accounting";
import Proposals from "@/pages/Proposals";
import ProposalDetail from "@/pages/ProposalDetail";
import PublicProposal from "@/pages/PublicProposal";
import Catalog from "@/pages/Catalog";
import Settings from "@/pages/Settings";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const navigate = useNavigate();
  const ranRef = React.useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/", { replace: true });
      return;
    }
    const sid = decodeURIComponent(m[1]);
    (async () => {
      try {
        const r = await api.post("/auth/session", { session_id: sid });
        window.history.replaceState({}, document.title, "/app/dashboard");
        navigate("/app/dashboard", { replace: true, state: { user: r.data.user } });
      } catch (e) {
        const msg = e?.response?.status === 403 ? "unauthorized" : "";
        navigate(`/?error=${msg}`, { replace: true });
      }
    })();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-zinc-500 label-eyebrow">Establishing session…</div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="label-eyebrow text-zinc-500">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" state={{ from: location }} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/proposal/:token" element={<PublicProposal />} />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="projects" element={<Projects />} />
        <Route path="projects/:id" element={<ProjectDetail />} />
        <Route path="takeoff" element={<Takeoff />} />
        <Route path="estimator" element={<Estimator />} />
        <Route path="proposals" element={<Proposals />} />
        <Route path="proposals/:id" element={<ProposalDetail />} />
        <Route path="employees" element={<Employees />} />
        <Route path="timesheet" element={<Timesheet />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="invoices/:id" element={<InvoiceDetail />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="accounting" element={<Accounting />} />
        <Route path="catalog" element={<Catalog />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="bottom-right" />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}
